import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  PermissionProvider,
  ROLE_IDS,
  RoleId,
  ROLES,
  RoleType,
} from '@efexteam/permission-manager';
import { selectProfileData } from '@pages/profile/store/selectors';
import { selectAuthState } from '../../../layout/login/auth/selectors';
import { UserProfileResponse } from '../types';
import { PermissionManagerProps } from './types';

interface CustomPermissionManagerProps extends PermissionManagerProps {
  roleId?: RoleId;
}

/**
 * A provider component that manages user permissions based on their role.
 * It transforms user profile data into a format compatible with PermissionProvider.
 */
export const PermissionManagerProvider = ({
  children,
}: PermissionManagerProps) => {
  const profile = useSelector(selectProfileData);
  const authState = useSelector(selectAuthState);

  const getRolesFromUser = useCallback(
    (user: UserProfileResponse): RoleType[] => {
      if (!user?.legalRepresentative?.roleId) {
        return [ROLES.READ_ONLY];
      }

      const { roleId } = user.legalRepresentative;
      return roleId ? [ROLE_IDS[roleId]] : [ROLES.READ_ONLY];
    },
    []
  );

  const profileWithRoles = useMemo(() => {
    if (!profile) return undefined;

    const roles =
      authState.roles && authState.roles.length > 0
        ? (authState.roles as RoleType[])
        : getRolesFromUser(profile);

    return {
      ...profile,
      roles,
      permissions: authState.permissions || [],
    };
  }, [profile, authState, getRolesFromUser]);

  return (
    <PermissionProvider user={profileWithRoles}>{children}</PermissionProvider>
  );
};

/**
 * A custom provider component that accepts a roleId prop to override the user's role.
 * Useful for testing or specific role-based scenarios.
 */
export const CustomPermissionManagerProvider = ({
  children,
  roleId,
}: CustomPermissionManagerProps) => {
  const profile = useSelector(selectProfileData);

  const getRoleFromId = useCallback((providedRoleId?: RoleId): RoleType[] => {
    if (!providedRoleId) {
      return [ROLES.READ_ONLY];
    }

    const roleFromId = ROLE_IDS[providedRoleId];
    return roleFromId ? [roleFromId] : [ROLES.READ_ONLY];
  }, []);

  const profileWithRoles = useMemo(() => {
    if (!profile) return undefined;

    return {
      ...profile,
      roles: getRoleFromId(roleId),
    };
  }, [profile, roleId, getRoleFromId]);

  return (
    <PermissionProvider user={profileWithRoles}>{children}</PermissionProvider>
  );
};

export default PermissionManagerProvider;
